import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { HandlingUnitOverview } from '../../models';
import { HandlingUnitOverviewService } from './handling-unit-overview.service';
import {
  HandlingUnitOverviewInitialState,
  HandlingUnitOverviewLoad,
  HandlingUnitTrackingLoad,
} from './handling-unit-overview.actions';
import { Tracking } from '../../../../models';
import { Error, CustomHttpErrorResponse } from '../../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface HandlingUnitOverviewStateModel {
  overview: HandlingUnitOverview;
  loaded: boolean;
  tracking: Tracking[];
}

const initialState: HandlingUnitOverviewStateModel = {
  overview: null,
  loaded: false,
  tracking: [],
};

@State<HandlingUnitOverviewStateModel>({
  name: 'handlingUnitOverview',
  defaults: initialState,
})
@Injectable()
export class HandlingUnitOverviewState {
  constructor(
    private handlingUnitOverviewService: HandlingUnitOverviewService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: HandlingUnitOverviewStateModel): HandlingUnitOverviewStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: HandlingUnitOverviewStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getTracking(state: HandlingUnitOverviewStateModel): Tracking[] {
    return state.tracking;
  }

  @Selector()
  static getOverview(state: HandlingUnitOverviewStateModel): HandlingUnitOverview {
    return state.overview;
  }

  @Action(HandlingUnitOverviewLoad)
  getHandlingUnitOverview(
    ctx: StateContext<HandlingUnitOverviewStateModel>,
    { payload }: HandlingUnitOverviewLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.handlingUnitOverviewService.getOverviewData(payload).pipe(
      map((overview: HandlingUnitOverview) => {
        ctx.patchState({ overview, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HandlingUnitTrackingLoad)
  getHandlingUnitTracking(
    ctx: StateContext<HandlingUnitOverviewStateModel>,
    { payload }: HandlingUnitTrackingLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.handlingUnitOverviewService.getTracking(payload).pipe(
      map((tracking: Tracking[]) => {
        ctx.patchState({ tracking, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HandlingUnitOverviewInitialState)
  setInitialState(ctx: StateContext<HandlingUnitOverviewStateModel>): void {
    ctx.setState(initialState);
  }
}
